import type { ReactElement } from "react";
import type { GroupBase, StylesConfig } from "react-select";
import type { LoadOptions } from "react-select-async-paginate";
import { colors } from "../../../colors";

type SelectCommonProps = {
    isClearable?: boolean;
    disabled?: boolean;
    label?: string;
    menuPortalTargetIsNotBody?: boolean;
    placeholder: string;
    noOptionsMessage: string;
    error?: boolean;
    errorLabel?: string;
    isSearchable?: boolean;
    isMulti?: boolean;
    required?: boolean;
    customZIndex?: number;
};

export type SelectMulti = {
    'MULTI': string[]
    'NOT_MULTI': string
    'MULTI_ASYNC': BaseOption[]
    'NOT_MULTI_ASYNC': BaseOption
}

export interface BaseOption {
    label: string | ReactElement;
    value: string;
    description?: string;
}

// interface Options<multiple extends 'MULTI' | 'NOT_MULTI' | 'MULTI_ASYNC' | 'NOT_MULTI_ASYNC'> {
//     defaultValue?: SelectMulti[multiple];
//     onChange?: (value: SelectMulti[multiple]) => void;
// }

export interface AsyncSelectProps extends SelectCommonProps {
    loadOptions: (inputValue: string) => Promise<BaseOption[]>;
    defaultValue?: BaseOption | BaseOption[] | null;
    onChange?: (value: BaseOption | BaseOption[] | null) => void;
    onMenuScrollToBottom?: (event: WheelEvent | TouchEvent) => void;
    menuIsOpen?: boolean;
};

export type Additional = { page: number };

export interface PaginateSelectProps<T> extends SelectCommonProps {
    loadOptions: LoadOptions<T, GroupBase<T>, Additional>;
    defaultValue?: { label: string, value: T } | { label: string, value: T }[] | null;
    onChange?: (value: { label: string, value: T } | null) => void;
    onMenuScrollToBottom?: (event: WheelEvent | TouchEvent) => void;
    menuIsOpen?: boolean;
    additional?: Additional;
    cacheUniqs?: any[];
};

export interface StaticSelectProps extends SelectCommonProps {
    options: BaseOption[];
    defaultValue?: string | string[] | null;
    value?: string | string[] | null;
    onChange?: (value: string | string[] | null) => void;
    disabledOptions?: BaseOption[];
};


export interface GroupSelectProps extends SelectCommonProps {
    options: { label: string; options: BaseOption[] }[];
    defaultValue?: string | string[] | null;
    onChange?: (value: string | string[] | null) => void;
}

export function getMadfarmDefaultSelectTheme<
    OptionType,
    IsMulti extends boolean = boolean,
    Group extends GroupBase<OptionType> = GroupBase<OptionType>
>(error?: boolean, customZIndex?: number): StylesConfig<OptionType, IsMulti, Group> {
    return madfarmDefaultSelectTheme(error, customZIndex) as unknown as StylesConfig<OptionType, IsMulti, Group>;
}

export const madfarmDefaultSelectTheme = (error?: boolean, customZIndex?: number): StylesConfig => {
    const zIndex = customZIndex ?? 40;
    return {
        control: (baseStyles, state) => ({
            ...baseStyles,
            minHeight: "48px",
            width: "100%",
            borderRadius: "8px",
            backgroundColor: state.isDisabled ? 'rgba(242,242,250,1)' : colors.white,
            boxShadow: "0px 1px 2px 0px hsla(220, 29%, 5%, 0.05)",
            border:
                error ? `1px solid ${colors.red[500]}`
                    : `1px solid ${colors.neutral[300]}`,
            fontFamily: "Urbanist",
            padding: "0px 12px",
            outline: state.isFocused ? "none" : "none",
            "&:hover": {
                border: state.isFocused
                    ? `1px solid ${colors.brandPrimary[700]}`
                    : `1px solid ${colors.neutral[300]}`,
                outline: state.isFocused ? "none" : "none",
            },
        }),
        placeholder: (baseStyles) => ({
            ...baseStyles,
            fontSize: "16px",
            lineHeight: "150%",
            color: colors.neutral[500],
            fontFamily: "Manrope",
            fontWeight: 500,
            textAlign: 'left',
        }),
        input: (baseStyles) => ({
            ...baseStyles,
            fontSize: "16px",
            lineHeight: "150%",
            color: colors.neutral[900],
            fontFamily: "Manrope",
            fontWeight: 500,
            textAlign: 'left',
        }),
        singleValue: (baseStyles) => ({
            ...baseStyles,
            fontSize: "16px",
            lineHeight: "150%",
            color: colors.neutral[900],
            fontFamily: "Manrope",
            fontWeight: 500,
            textAlign: 'left',
        }),
        option: (baseStyles, state) => ({
            ...baseStyles,
            fontSize: "16px",
            lineHeight: "150%",
            fontFamily: "Manrope",
            fontWeight: 500,
            backgroundColor: state.isSelected
                ? colors.brandPrimary[600] : colors.white,
            "&:hover": {
                backgroundColor: colors.brandPrimary[500],
                color: colors.white
            }
        }),
        container: (baseStyles) => ({
            ...baseStyles,
            width: '100%',
            maxWidth: '100%',
        }),
        menu: (baseStyles) => ({
            ...baseStyles,
            zIndex: zIndex,
        }),
        menuPortal: (baseStyles) => ({
            ...baseStyles,
            zIndex: zIndex,
        }),
        valueContainer: (baseStyles) => ({
            ...baseStyles,
            padding: 0,
            overflow: "auto",
        }),
        indicatorSeparator: () => ({
            display: "none",
        }),
        indicatorsContainer: (baseStyles) => ({
            ...baseStyles,
            alignItems: 'center',
        }),
        dropdownIndicator: () => ({
            color: colors.neutral[500],
            strokeWidth: "0 !important",
            outlineWidth: 0
        }),
        clearIndicator: () => ({
            color: colors.neutral[500]
        }),
        groupHeading: (baseStyles) => ({
            ...baseStyles,
            fontSize: '16px',
            lineHeight: '24px',
            textTransform: 'none',
            fontWeight: 600
        }),
        multiValue: (baseStyles) => ({
            ...baseStyles,
            fontSize: '16px',
            lineHeight: '22px',
            fontFamily: 'Manrope',
            fontWeight: 500,
            padding: '0px 8px',
            minWidth: 'auto',
            borderRadius: 8,
            backgroundColor: colors.brandPrimary[200],
            border: '1px solid ' + colors.brandPrimary[400],
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
        }),
        multiValueLabel: (baseStyles) => ({
            ...baseStyles,
            width: '100%',
            display: "block",
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 'calc(100% - 20px)',
        }),
        multiValueRemove: (baseStyles) => ({
            ...baseStyles,
            color: colors.neutral[500],
            "&:hover": {
                color: colors.brandPrimary[600],
                backgroundColor: colors.brandPrimary[200],
            },
        }),
    }
}